<script>
	import '../app.css';
	import { browser } from '$app/environment';
	import { afterNavigate, goto } from '$app/navigation';
	import { closeAllModals } from 'svelte-modals';
	import { onMount, onDestroy } from 'svelte';
	import GlobalLoadingOverlay from '$lib/services/loading/GlobalLoadingOverlay.svelte';
	import { loadThirdPartyScripts } from '$lib/utils/third-party.js';
	import { setHotjarPlatformTags } from '$lib/utils/hotjar';
	import { AOFL_PRODUCT_NAMES, PAGES, COOKIES } from '$lib/utils/constants';
	import { get } from 'svelte/store';
	import { app } from '$lib/stores/app/store';
	import { initializeAnalyticsCookiesStore } from '$lib/stores/app/actions';
	import { AnalyticsService } from '$lib/services/analytics';
	import { Sentry } from '$lib/services/sentry';
	import Cookies from 'js-cookie';

	afterNavigate(async (nav) => {
		const prevPage = nav?.from?.route?.id;
		const nextPage = nav?.to?.route?.id;

		Sentry.addBreadcrumb({
			category: 'navigation',
			message: `Navigated from ${prevPage} to ${nextPage}`,
			level: 'info',
		});
	});

	/**
	 * This function is used to redirect the user to the download app page if the user is a ABC Mouse App 2.0 user trying to access to the ABCMouse App 1.0
	 * Only redirects in specific scenarios, not on every page load
	 * TODO: needs to be refactored to move into the post auth service.
	 * @returns {void}
	 */
	function abc2DownloadAppInterceptor() {
		const isAbcMouse2_0Product = get(app).global.appProduct === AOFL_PRODUCT_NAMES.ABCMOUSE_2_0;
		const currentPath = window.location.pathname;
		const isAlreadyThere = currentPath === PAGES.INTERNAL.DOWNLOAD_APP;

		// Don't redirect if user is already on download-app page or not a 2.0 product
		if (isAlreadyThere || !isAbcMouse2_0Product) return;

		// Only redirect if user is on a path that should not be accessible for 2.0 users
		// For now, we'll be less aggressive and let users navigate normally
		// The RegistrationPathService will handle proper flow after login/subscription
		const shouldRedirect = false; // Disable automatic redirects for now

		if (shouldRedirect) {
			goto(PAGES.INTERNAL.DOWNLOAD_APP);
		}
	}

	onMount(async () => {
		if ($app?.meta?.isAmazonApp || $app?.meta?.isAndroidApp)
			document.documentElement.classList.add('fixed-font');

		initializeAnalyticsCookiesStore();
		AnalyticsService.onMount();
		setHotjarPlatformTags($app?.meta);

		window.addEventListener('pageshow', (e) => {
			if (e.persisted) closeAllModals();
		});

		const shouldAddAnalyticsScripts =
			browser &&
			!window?.navigator?.globalPrivacyControl &&
			!$app?.meta?.inApp &&
			$app?.global?.analyticsCookiesEnabled &&
			$app?.global?.targetingCookiesEnabled;

		if (shouldAddAnalyticsScripts) {
			loadThirdPartyScripts();
		}

		// This is a custom interceptor to redirect to the download app page if the user is a ABC Mouse App 2.0 user trying to access to the ABCMouse App 1.0
		abc2DownloadAppInterceptor();

		// MKTABC-3904 - Track sourced traffic from /landing
		await handleLegacySourcedTraffic();
	});

	/**
	 * Handles legacy sourced traffic
	 * @returns {Promise<void>} Promise that resolves when the legacy sourced traffic is handled
	 */
	async function handleLegacySourcedTraffic() {
		if (Cookies.get(COOKIES.REFERRER) === '/landing') {
			await AnalyticsService.trackClientDebug({
				message: 'Referrer: /landing',
			});
		}
	}

	onDestroy(() => {
		if (browser) AnalyticsService.onDestroy();
	});

	const appVersion = __APP_VERSION__;
</script>

<!-- TODO: move this to be config driven: debug build -->
<svelte:head>
	<meta
		name="app-version"
		content={appVersion}
	/>
	<script
		crossorigin
		src="https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js"
		defer
	></script>
	<script></script>
	<!-- <script src="https://seo-games-abc-mouse.ngrok.dev/vorlon.js"></script> -->
	<script
		async
		defer
		src="//client.perimeterx.net/PXTU0h5FRQ/main.min.js"
	></script>
</svelte:head>

<slot />

<GlobalLoadingOverlay />
